import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, Tags, TrendingUp, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

function StatCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [recentMovements, setRecentMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, catRes, movRes] = await Promise.all([
        fetch('/inventory-app/api/dashboard'),
        fetch('/inventory-app/api/dashboard/categories'),
        fetch('/inventory-app/api/stock-movements')
      ]);
      
      const statsData = await statsRes.json();
      const catData = await catRes.json();
      const movData = await movRes.json();
      
      setStats(statsData);
      setCategories(catData);
      setRecentMovements(movData.slice(0, 5));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Overview of your inventory</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          title="Total Products" 
          value={stats?.total_products || 0} 
          icon={Package} 
          color="bg-primary-500"
          subtitle="Across all categories"
        />
        <StatCard 
          title="Low Stock Items" 
          value={stats?.low_stock_items || 0} 
          icon={AlertTriangle} 
          color="bg-amber-500"
          subtitle="Need attention"
        />
        <StatCard 
          title="Categories" 
          value={stats?.total_categories || 0} 
          icon={Tags} 
          color="bg-emerald-500"
          subtitle="Product groups"
        />
        <StatCard 
          title="Inventory Value" 
          value={formatCurrency(stats?.inventory_value || 0)} 
          icon={TrendingUp} 
          color="bg-violet-500"
          subtitle="Total valuation"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Stock by Category</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={categories}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
              />
              <Bar dataKey="total_quantity" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Category Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categories}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="product_count"
                nameKey="name"
              >
                {categories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-4 justify-center">
            {categories.map((cat, index) => (
              <div key={cat.name} className="flex items-center gap-1">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-xs text-slate-600">{cat.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-slate-900">Recent Stock Movements</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Product</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Quantity</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Previous</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">New</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Reason</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
              </tr>
            </thead>
            <tbody>
              {recentMovements.map((movement) => (
                <tr key={movement.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-3 px-4 text-sm text-slate-900">{movement.product_name}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      movement.type === 'IN' ? 'bg-emerald-100 text-emerald-700' :
                      movement.type === 'OUT' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {movement.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-900">{movement.quantity}</td>
                  <td className="py-3 px-4 text-sm text-slate-500">{movement.previous_quantity}</td>
                  <td className="py-3 px-4 text-sm font-medium text-slate-900">{movement.new_quantity}</td>
                  <td className="py-3 px-4 text-sm text-slate-500">{movement.reason}</td>
                  <td className="py-3 px-4 text-sm text-slate-500">
                    {new Date(movement.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;